import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { resources } from '@/mocks/resources';

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [selectedCategory, setSelectedCategory] = useState(slug || '');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [sortBy, setSortBy] = useState('downloads');

  useEffect(() => {
    if (slug) {
      setSelectedCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === selectedCategory);

  const filteredResources = useMemo(() => {
    let filtered = resources;

    if (selectedCategory) {
      filtered = filtered.filter((r) => r.category === selectedCategory);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q)
      );
    }

    if (selectedPlatform !== 'all') {
      filtered = filtered.filter((r) => r.platform.includes(selectedPlatform));
    }

    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'downloads') return b.downloads - a.downloads;
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'newest') return b.updateDate.localeCompare(a.updateDate);
      return 0;
    });

    return filtered;
  }, [selectedCategory, searchQuery, selectedPlatform, sortBy]);

  const formatDownloads = (num: number) => {
    if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
    return num.toString();
  };

  const platforms = ['all', 'Windows', 'macOS', 'Linux', 'Android', 'iOS'];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory ? currentCategory.name : (searchQuery ? `搜索: ${searchQuery}` : '全部分类') },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {currentCategory ? currentCategory.name : (searchQuery ? `搜索: "${searchQuery}"` : '全部分类')}
              </h1>
              {currentCategory && (
                <p className="text-sm text-foreground-600">{currentCategory.description}</p>
              )}
              {searchQuery && !currentCategory && (
                <p className="text-sm text-foreground-600">共找到 {filteredResources.length} 个相关资源</p>
              )}
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="w-full lg:w-56 flex-shrink-0">
                <div className="sticky top-20 space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3">资源分类</h3>
                    <div className="space-y-1">
                      <button
                        onClick={() => setSelectedCategory('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          !selectedCategory
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        全部分类
                      </button>
                      {categories.map((cat) => (
                        <button
                          key={cat.slug}
                          onClick={() => setSelectedCategory(cat.slug)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                            selectedCategory === cat.slug
                              ? 'bg-primary-100 text-primary-700 font-medium'
                              : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          <span className="flex items-center justify-between">
                            <span className="truncate">{cat.name}</span>
                            <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">{cat.count}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3">平台筛选</h3>
                    <div className="flex flex-wrap gap-2">
                      {platforms.map((p) => (
                        <button
                          key={p}
                          onClick={() => setSelectedPlatform(p)}
                          className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                            selectedPlatform === p
                              ? 'bg-accent-500 text-white'
                              : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          {p === 'all' ? '全部' : p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-900">{filteredResources.length}</strong> 款资源
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500">排序:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-xs border border-background-200 rounded-lg px-3 py-1.5 bg-background-50 text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-400"
                    >
                      <option value="downloads">下载量</option>
                      <option value="rating">评分</option>
                      <option value="newest">最新</option>
                    </select>
                  </div>
                </div>

                {filteredResources.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-600 text-sm">没有找到匹配的资源</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-product-shop>
                    {filteredResources.map((res) => (
                      <div
                        key={res.id}
                        className="p-5 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-300 transition-all"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                            <i className={`${res.icon} text-lg`}></i>
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-foreground-900 truncate">{res.name}</h3>
                            <p className="text-xs text-foreground-500 mt-0.5">
                              {res.version} · {res.size}
                            </p>
                          </div>
                        </div>

                        <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                          {res.description}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                            <span className="text-xs font-medium text-foreground-700">{res.rating}</span>
                            <span className="text-xs text-foreground-400 ml-1">
                              {formatDownloads(res.downloads)}次下载
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {res.platform.slice(0, 3).map((p) => (
                              <span
                                key={p}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap"
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}